import { createSlice } from '@reduxjs/toolkit';

// Function to safely get item from storage
const getItemFromStorage = (key) => {
  try {
    // Check if we're in a WebView environment
    if (window.ReactNativeWebView) {
      // In WebView, try to get from window object
      return window[key] || null;
    }
    // In browser, use localStorage
    return localStorage.getItem(key);
  } catch (error) {
    console.warn('Storage access error:', error);
    return null;
  }
};

// Function to safely set item in storage
const setItemInStorage = (key, value) => {
  try {
    if (window.ReactNativeWebView) {
      window[key] = value;
    } else {
      localStorage.setItem(key, value);
    }
  } catch (error) {
    console.warn('Storage access error:', error);
  }
};

// Function to safely remove item from storage
const removeItemFromStorage = (key) => {
  try {
    if (window.ReactNativeWebView) {
      window[key] = null;
    } else {
      localStorage.removeItem(key);
    }
  } catch (error) {
    console.warn('Storage access error:', error);
  }
};

// Function to check if token exists in storage
const getTokenFromStorage = () => {
  return getItemFromStorage('token');
};

// Check if a token exists in storage on initial load
const token = getTokenFromStorage();
const userStr = getItemFromStorage('user');
let user = null;

// Add debugging
console.log("Auth initialization - Token exists:", !!token);

// Validate token and user data
try {
  if (userStr) {
    user = JSON.parse(userStr);
    console.log("Auth initialization - User parsed successfully");
  }
} catch (error) {
  console.error("Failed to parse user from storage:", error);
  // If user parsing fails, consider token invalid
  removeItemFromStorage('token');
  removeItemFromStorage('user');
}

// More robust check - only authenticate if both token and user exist
const isAuthenticated = !!(token && user);
console.log("Auth initialization - Final authentication state:", isAuthenticated);

const initialState = {
  user: user,
  token: token,
  isAuthenticated: isAuthenticated,
  loading: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    loginSuccess: (state, action) => {
      state.loading = false;
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.token = action.payload.token;
      setItemInStorage('token', action.payload.token);
      setItemInStorage('user', JSON.stringify(action.payload.user));
    },
    loginFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      removeItemFromStorage('token');
      removeItemFromStorage('user');
    },
    clearError: (state) => {
      state.error = null;
    },
    updateProfilePicture: (state, action) => {
      if (state.user) {
        state.user.profilePicture = action.payload;
        setItemInStorage('user', JSON.stringify(state.user));
      }
    },
  },
});

export const {
  loginStart,
  loginSuccess,
  loginFailure,
  logout,
  clearError,
  updateProfilePicture,
} = authSlice.actions;

export default authSlice.reducer; 